import React from "react";
import {
  redirect,
  useLoaderData,
  useLocation,
  useNavigate,
  useRouteError,
} from "react-router-dom";
import "./styles.css";
import GenericErrorDisplay from "../../generic-error";
import OutputResource from "../../../dtos/resource";
import { useEffect, useState } from "react";
import OutputNewsSummary from "../../../dtos/news/summary";
import ResourceDisplay from "../../../components/resource-display";
import { IMAGE_HOST } from "../../../server/config";
import { Pagination, PaginationItem } from "@mui/material";
import STYLES_CONSTANTS from "../../../styles/index";
import useWindowDimensions from "../../../hooks/useWindowDimensions";
import SearchBox from "../../../components/inputs/search-box";
import NewsSummaryCard from "../../../components/news-summary-card/index.jsx";
import ResourceService from "../../../service/resource";
import NewsService from "../../../service/news";

const DEFAULT_NEWS_PER_PAGE = 8;
const RECORD_HISTORY_ON_PAGE_CHANGE = false;

const resourceKeys = {
  cover: "news-list-cover",
  pageTitle: "news-list-title",
  pageSubtitle: "news-list-subtitle",
  backgroundImage: "image-paper-white",
  aboutLinie: "about-nonews-liniealbastra",
  aboutPeople: "about-nonews-people",
};

type LoaderType = [
  [number, number, string | undefined],
  { [key: string]: OutputResource },
  number,
  OutputNewsSummary[]
];

export async function loader({ request }: any): Promise<LoaderType> {
  const url = new URL(request.url);

  const pageString = url.searchParams.get("page");
  let page: number;
  if (pageString === null) page = 1;
  else page = parseInt(pageString!);
  if (isNaN(page)) redirect("/not-found");

  const sizeString = url.searchParams.get("size");
  let size: number;
  if (sizeString === null) size = DEFAULT_NEWS_PER_PAGE;
  else size = parseInt(sizeString);
  if (isNaN(size)) redirect("/not-found");

  const searchString = url.searchParams.get("search");
  const search = searchString === null ? undefined : searchString;

  const resourcesPromise = new ResourceService().read.byIds(
    Object.values(resourceKeys)
  );

  try {
    const newsService = new NewsService();

    const newsListPromise = newsService.read.list(page, size, search);
    const totalNewsCountPromise = newsService.read.count(search);

    return [
      [page, size, search],
      await resourcesPromise,
      await totalNewsCountPromise,
      await newsListPromise,
    ];
  } catch (error: any) {
    if (error.response && error.response.status === 404) {
      return [[page, size, search], await resourcesPromise, 0, []];
    } else {
      console.error("Error loading news list:", error);
      throw error;
    }
  }
}

export function Component() {
  const [
    [currentPageNumber, requestedPageSize, searchQuerry],
    resources,
    totalNewsCount,
    newsList,
  ] = useLoaderData() as LoaderType;
  const [pageCount, setPageCount] = useState<number>(0);

  const windowsDimensions = useWindowDimensions();

  useEffect(
    () =>
      setPageCount(
        Math.floor(totalNewsCount / requestedPageSize) +
          (totalNewsCount % requestedPageSize === 0 ? 0 : 1)
      ),
    [totalNewsCount, requestedPageSize]
  );

  const navigate = useNavigate();
  const location = useLocation();

  function handlePaginationChange(
    event: React.ChangeEvent<unknown>,
    value: number
  ) {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("page", value.toString());

    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: RECORD_HISTORY_ON_PAGE_CHANGE,
    });
  }

  function handleSearchSubmit(searchQuery: string) {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("search", searchQuery.toString().trim());
    searchParams.set("page", "1");

    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: RECORD_HISTORY_ON_PAGE_CHANGE,
    });
  }

  // if (newsList.length === 0) {
  //   return (
  //     <div id="page-nonews">
  //     <div className="big-container">
  //       <h2 className="text-container">Niciun rezultat nu a fost gasit!</h2>
  //       <div className="line-container"><ResourceDisplay resource={aboutLinie}/></div>
  //     </div>
  //     <div className="image-container"><ResourceDisplay resource={aboutPeople}/></div>
  //     </div>

  //   )};
  return (
    <div
      id="page-news"
      style={
        (resources[resourceKeys.backgroundImage]
          ? {
              "--background-image-url":
                "url(" +
                IMAGE_HOST +
                resources[resourceKeys.backgroundImage].value +
                ")",
            }
          : {}) as React.CSSProperties
      }
    >
      <section className="header content-container">
        <ResourceDisplay
          resource={resources[resourceKeys.cover]}
          className="cover"
        />
        <ResourceDisplay
          resource={resources[resourceKeys.pageTitle]}
          wrapper="h1"
          className="page-title"
        />
        <ResourceDisplay
          resource={resources[resourceKeys.pageSubtitle]}
          className="page-subtitle"
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            alignSelf: "stretch",
            backgroundColor: "white",
          }}
          className="surface search"
        >
          <SearchBox
            onSearch={handleSearchSubmit}
            initialValue={searchQuerry}
            // style={{ "--surface-color": "var(--color-light)" }}
          />
        </div>
      </section>
      <section className="news content-container">
        {newsList.length === 0 ? (
          <div id="page-nonews">
            <div className="big-container">
              <h2 className="text-container">
                Niciun rezultat nu a fost găsit!
              </h2>
              <div className="line-container">
                <ResourceDisplay
                  resource={resources[resourceKeys.aboutLinie]}
                />
              </div>
            </div>
            <div className="image-container">
              <ResourceDisplay resource={resources[resourceKeys.aboutPeople]} />
            </div>
          </div>
        ) : (
          <>
            <Pagination
              className="pagination"
              onChange={handlePaginationChange}
              renderItem={(item) => (
                <PaginationItem
                  sx={{
                    fontFamily: STYLES_CONSTANTS["font-family"],
                    fontWeight: item.selected ? "bold" : "regular",
                    color: "white",
                  }}
                  {...item}
                />
              )}
              count={pageCount}
              page={currentPageNumber}
              defaultPage={1}
              siblingCount={windowsDimensions.width > 600 ? 1 : 0}
              boundaryCount={windowsDimensions.width > 600 ? 1 : 0}
              size="large"
              showFirstButton={windowsDimensions.width > 400}
              showLastButton={windowsDimensions.width > 400}
            />
            {newsList.map((newsSummary: any) => (
              <NewsSummaryCard
                newsSummary={newsSummary}
                key={newsSummary.id}
              />
            ))}
            <Pagination
              className="pagination"
              onChange={handlePaginationChange}
              renderItem={(item) => (
                <PaginationItem
                  sx={{
                    fontFamily: STYLES_CONSTANTS["font-family"],
                    fontWeight: item.selected ? "bold" : "regular",
                    color: item.selected
                      ? "var(--color-fasmr-blue)"
                      : "var(--color-dark)",
                  }}
                  {...item}
                />
              )}
              count={pageCount}
              page={currentPageNumber}
              defaultPage={1}
              siblingCount={windowsDimensions.width > 600 ? 1 : 0}
              boundaryCount={windowsDimensions.width > 600 ? 1 : 0}
              size="large"
              showFirstButton={windowsDimensions.width > 400}
              showLastButton={windowsDimensions.width > 400}
            />
          </>
        )}
      </section>
    </div>
  );
}

Component.displayName = "page-noutati";

export function ErrorBoundary() {
  const error = useRouteError();
  return <GenericErrorDisplay error={error} />;
}
ErrorBoundary.displayName = "page-noutati-error";
